import { auth } from '../firebase';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:3800";

/**
 * Get franchisee registration and compliance status report
 * @returns {Promise<{data: Array, summary: Object|null}>} Report rows and summary metadata
 * @throws {Error} Throws an error if the fetch fails or the user is not authenticated
 */
export const getFranchiseeRegistrationReport = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();
    const url = `${BASE_URL}/reports/franchisee-registration-status`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return {
      data: responseData.data || [],
      summary: responseData.summary || null
    };

  } catch (error) {
    console.error("Error fetching franchisee registration report:", error);
    throw error;
  }
};

/**
 * Get report of completed jobs without invoices and/or RCTIs
 * @param {Object} options - Filter options
 * @param {string} options.startDate - Filter jobs completed on or after this date (ISO string)
 * @param {string} options.endDate - Filter jobs completed on or before this date (ISO string)
 * @param {string} options.missingType - Filter by what's missing: 'invoice', 'rcti', 'both', 'any' (default: 'any')
 * @returns {Promise<Object>} Report data with data array and summary
 * @throws {Error} Throws an error if the fetch fails or the user is not authenticated
 */
/**
 * Download the Franchisee Compliance Register PDF.
 * Triggers a browser file download.
 * @throws {Error} if user is not authenticated or the request fails
 */
export const downloadComplianceRegisterPDF = async () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();
  const url = `${BASE_URL}/reports/compliance-register-pdf`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${idToken}` },
  });

  if (!response.ok) {
    // Try to parse error JSON
    let message = `HTTP error! status: ${response.status}`;
    try {
      const data = await response.json();
      message = data.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }

  // Trigger browser download
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  const today = new Date().toISOString().split('T')[0];
  a.download = `Franchisee_Compliance_Register_${today}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
};

export const getJobsWithoutInvoicesReport = async (options = {}) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  try {
    const idToken = await user.getIdToken();

    // Build query string from options
    const queryParams = new URLSearchParams();
    if (options.startDate) queryParams.append('startDate', options.startDate);
    if (options.endDate) queryParams.append('endDate', options.endDate);
    if (options.missingType) queryParams.append('missingType', options.missingType);

    const queryString = queryParams.toString();
    const url = `${BASE_URL}/reports/jobs-without-invoices${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    const responseData = await response.json();

    if (!response.ok || responseData.status !== 'success') {
      throw new Error(responseData.message || `HTTP error! status: ${response.status}`);
    }

    return {
      data: responseData.data || [],
      summary: responseData.summary || {
        totalCompleted: 0,
        totalMissing: 0,
        missingInvoice: 0,
        missingRcti: 0,
        missingBoth: 0
      }
    };

  } catch (error) {
    console.error("Error fetching jobs without invoices report:", error);
    throw error;
  }
};
